import { Checkbox, ListItemText, MenuItem, Select } from '@mui/material';
import {
  getSubjectLineFields,
  SubjectLineField
} from '../../../Api/Admin/ProjectManagementConfigs/projectManagementConfigsApi';
import { CatalogUnavailableNote } from './CatalogUnavailableNote';
import { useCatalog } from './useCatalog';

const TITLE = 'At Risk Widget: Subject Line Prefix';
const DESCRIPTION = 'Select a field(s) to replace the Address portion of an At Risk item subject line.';
const ADDRESS_PLACEHOLDER = 'Address';

// The subject line inherits through the portal's curated Sub Accounts list, while the risk tiers
// inherit through Salesforce — so "Inherited" means something different on each half of this form.
const INHERITED_NOTE =
  "Inherited from this account's parent in the portal's Sub Accounts list. Selecting fields here overrides it.";

// Stated in our own words: an API without this endpoint answers "Not Found", which reads as a broken page.
const CATALOG_ERROR = 'The list of fields could not be loaded, so the current selection cannot be changed.';
const CATALOG_EMPTY = 'No fields are available to select.';

// A multiple Select hands back a comma-joined string when the value arrives from the browser, not the menu.
// Blanks are dropped: an empty string would otherwise become a field id the API rejects, taking the tiers down with it.
const toSelection = (value: string[] | string): string[] =>
  typeof value === 'string' ? value.split(',').filter(Boolean) : value;

export interface SubjectLinePrefixFieldProps {
  value: string[];
  onChange: (fields: string[]) => void;
  showInheritedNote: boolean;
}

export const SubjectLinePrefixField = ({ value, onChange, showInheritedNote }: SubjectLinePrefixFieldProps) => {
  const { fields: catalog, loading, failed, reload } = useCatalog<SubjectLineField>(getSubjectLineFields);

  // Reachable when the catalogue could not be loaded: a stored selection still has to render as something.
  const fieldLabel = (id: string) => catalog.find((field) => field.id === id)?.label ?? id;

  const renderSelection = (selected: string[]) =>
    selected.length === 0 ? ADDRESS_PLACEHOLDER : selected.map(fieldLabel).join(', ');

  return (
    <div
      className="py-5 border-t border-gray-200"
      data-cy="subject-line-section">
      <label
        id="subject-line-fields-label"
        htmlFor="subject-line-fields"
        className="text-sm font-medium text-gray-700">
        {TITLE}
      </label>
      <p className="text-sm text-gray-500 mt-1 mb-3">{DESCRIPTION}</p>
      <Select
        multiple
        displayEmpty
        labelId="subject-line-fields-label"
        inputProps={{ id: 'subject-line-fields' }}
        size="small"
        className="w-96"
        disabled={catalog.length === 0}
        value={value}
        onChange={(e) => onChange(toSelection(e.target.value))}
        renderValue={renderSelection}
        style={{ fontSize: '.875rem' }}
        data-cy="subject-line-fields-select">
        {catalog.map((field) => (
          <MenuItem
            key={field.id}
            value={field.id}
            data-cy={`subject-line-field-${field.id}`}>
            <Checkbox
              size="small"
              checked={value.includes(field.id)}
            />
            <ListItemText
              primary={field.label}
              primaryTypographyProps={{ fontSize: '.875rem' }}
            />
          </MenuItem>
        ))}
      </Select>
      {!loading && catalog.length === 0 && (
        <CatalogUnavailableNote
          failed={failed}
          failedMessage={CATALOG_ERROR}
          emptyMessage={CATALOG_EMPTY}
          onRetry={reload}
          testIdPrefix="subject-line-catalog"
        />
      )}
      {showInheritedNote && (
        <p
          className="text-sm text-gray-500 mt-2"
          data-testid="subject-line-inherited-note">
          {INHERITED_NOTE}
        </p>
      )}
    </div>
  );
};
